// externals
import type { ComponentPropsWithoutRef, MutableRefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import { Box } from '@interstate/components/Box';
import { CardHeader } from '@interstate/components/Card';
import type { CheckBoxEventValue } from '@interstate/components/CheckBox';
import { CheckBox } from '@interstate/components/CheckBox';
import { Grid } from '@interstate/components/Grid';
import { Typography } from '@interstate/components/Typography';
import { dealLockHooks, PaymentLoading } from '@makemydeal/dr-dash-components';
import { LoadingSectionParent } from '@makemydeal/dr-shared-components';
import type { FeeOverride } from '@makemydeal/dr-platform-types';
import { LEASE } from '@makemydeal/dr-platform-types';

// styles
import { FeeCardContainer, FeeDeeDetailsContainer } from './FeesCard.style';

// selectors
import * as dueAtSigningActionCreators from '@makemydeal/dr-dash-store/actions/dueAtSigningActionCreators';
import * as feesUtils from '@makemydeal/dr-dash-store/utils/feesUtils';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';

// components
import { AccordionDetails } from '@interstate/components/AccordionGroup';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { FeesCategoriesType } from '@makemydeal/dr-shared-types';
import { FeesCardHeader, StretchHeightCard } from '../deskingActivity/DeskingCard.style';
import FeesForm from './FeesForm';
import type { CustomFeeItem } from './types';
import { getCustomFeeItemsAdapter } from './utils';

type FeesCardProps = ComponentPropsWithoutRef<'div'> & {
    isFeesDirtyRef?: MutableRefObject<boolean>;
    isInsideSlideOut?: boolean;
    shouldTriggerValidation?: boolean;
};

/* istanbul ignore next - default parameter branch */
export const FeesCard: FC<FeesCardProps> = ({
    isFeesDirtyRef,
    isInsideSlideOut = false,
    shouldTriggerValidation = false,
    ...props
}) => {
    const dispatch = useDispatch();

    const govFees = useSelector(offerSelectors.getItemizedGovernmentFees);
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const lenderFees = useSelector(offerReduxSelectors.getItemizedLenderFees);
    const dealerFees = useSelector(offerSelectors.getItemizedDealerFees);
    const isAllFeesCapitalized = useSelector(feesUtils.isTermAllFeesCapitalized);

    const dueAtSigningEnhancements_E40674 = useSelector(featureToggleSelectors.enableDueAtSigningEnhancements);
    const isLocked = dealLockHooks.useIsDealLocked();

    const [deletedFees, setDeletedFees] = useState<Array<CustomFeeItem>>([]);
    const [allFeesCapitalized, setAllFeesCapitalized] = useState<boolean>(isAllFeesCapitalized);
    const [isCapAllFeeClickedManually, setIsAllFeeClickedManually] = useState<boolean>(false);

    const getCappedFeesStatus = useCallback((): boolean => {
        return allFeesCapitalized;
    }, [allFeesCapitalized]);

    useEffect(() => {
        setAllFeesCapitalized(isAllFeesCapitalized);
    }, [isAllFeesCapitalized]);

    const lenderFeeItems: CustomFeeItem[] = useMemo(() => {
        return getCustomFeeItemsAdapter(lenderFees, FeesCategoriesType.LENDER, deletedFees);
    }, [JSON.stringify(lenderFees)]);

    const govFeeItems: CustomFeeItem[] = useMemo(() => {
        return getCustomFeeItemsAdapter(govFees, FeesCategoriesType.GOVERNMENT, deletedFees);
    }, [JSON.stringify(govFees)]);

    const dealerFeeItems: CustomFeeItem[] = useMemo(() => {
        return getCustomFeeItemsAdapter(dealerFees, FeesCategoriesType.DEALER, deletedFees);
    }, [JSON.stringify(dealerFees)]);

    const isLease = offerType === LEASE;

    // Holds the AbortController for the most recent fee-driven payment recalculation. When the shopper
    // rapidly deletes/restores fees, each new save aborts the previous in-flight request so a slower,
    // now-stale response can't land after the latest one. The new request already carries the full
    // cumulative fee state, and the aborted request resolves to PAYMENT_ABORTED (which clears the
    // calculating state) rather than overwriting the latest result.
    const feesRecalcAbortControllerRef = useRef<AbortController | null>(null);

    /* istanbul ignore next - Callback invoked from within FeesForm component, tested via FeesForm integration tests */
    const onSave = (feesOverride: FeeOverride[], deletedFeesOnSave: Array<CustomFeeItem>, payment = true) => {
        let abortSignal: AbortSignal | undefined;
        if (payment) {
            feesRecalcAbortControllerRef.current?.abort();
            feesRecalcAbortControllerRef.current = new AbortController();
            abortSignal = feesRecalcAbortControllerRef.current.signal;
        }
        dispatch(offerActionCreators.updatedFeesOverride(feesOverride, payment, abortSignal));
        setDeletedFees(deletedFeesOnSave);
        setIsAllFeeClickedManually(false);
    };

    /* istanbul ignore next - Callback invoked from within FeesForm component, tested via FeesForm integration tests */
    const onReset = () => {
        dispatch(offerActionCreators.updatedFeesOverride([]));
    };

    const onCapitalizeAllFeesChange = (checked: boolean) => {
        dispatch(dueAtSigningActionCreators.capitalizeAllFees(checked));
        setAllFeesCapitalized(checked);
        setIsAllFeeClickedManually(true);
    };

    return (
        <LoadingSectionParent {...props}>
            <PaymentLoading testId="fees-card-loading-indicator" />
            <FeeCardContainer>
                <StretchHeightCard
                    className="stretch-height-card"
                    data-testid="fees-card"
                    header={
                        <FeesCardHeader>
                            {!isInsideSlideOut && <CardHeader title={<Typography variant="h3">Fees</Typography>} />}
                            {dueAtSigningEnhancements_E40674 && isLease && (
                                <CheckBox
                                    sx={{
                                        backgroundColor: 'inherit'
                                    }}
                                    aria-label="Capitalize All Fees"
                                    className="activities-desking-capitalize-all-fees"
                                    data-testid="activities-desking-capitalize-all-fees"
                                    id="capitalize-all-fees-id"
                                    label="Capitalize All Fees"
                                    name="Capitalize All Fees"
                                    value="1"
                                    checked={allFeesCapitalized}
                                    disabled={isLocked}
                                    onChange={(event: InterstateOnChangeEvent<CheckBoxEventValue>) =>
                                        onCapitalizeAllFeesChange(event.target.value.checked)
                                    }
                                />
                            )}
                        </FeesCardHeader>
                    }
                    content={
                        <Box display="flex" height="100%">
                            <Box width="100%" height="100%">
                                <Grid container rowGap={2} spacing={2} height="100%">
                                    <Grid xs={12} height="100%">
                                        <FeeDeeDetailsContainer>
                                            <AccordionDetails>
                                                <FeesForm
                                                    key="feescard-feesform"
                                                    offerType={offerType}
                                                    dealerFeesItems={dealerFeeItems}
                                                    governmentFeesItems={govFeeItems}
                                                    lenderFeesItems={lenderFeeItems}
                                                    onReset={onReset}
                                                    onSave={onSave}
                                                    isCapAllFeeClickedManually={isCapAllFeeClickedManually}
                                                    getCappedFeesStatus={getCappedFeesStatus}
                                                    isFeesDirtyRef={isFeesDirtyRef}
                                                    shouldTriggerValidation={shouldTriggerValidation}
                                                />
                                            </AccordionDetails>
                                        </FeeDeeDetailsContainer>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    }
                />
            </FeeCardContainer>
        </LoadingSectionParent>
    );
};

export default FeesCard;
